import { Controller } from '@hotwired/stimulus';

const ONBOARDING_SCROLL_RESTORE_KEY = 'onboarding:scroll-restore';

// Le slot du gabarit de message. Il porte le fragment mis en emphase — en
// pratique la donnée non fiable (une raison sociale, un nom de fichier…).
const HIGHLIGHT_SLOT = '%{highlight}';

/**
 * Contrôleur Stimulus pour gérer les modales de confirmation
 *
 * ⚠️ SÉCURITÉ — le message n'est JAMAIS du HTML (XSS QA #556).
 *
 * Le message transite par un attribut `data-*`. Or le navigateur DÉCODE les
 * entités en parsant un attribut, et Stimulus lit la valeur déjà décodée. Tout
 * échappement posé par le serveur est donc consommé par ce décodage : il en
 * fallait DEUX pour qu'il en survive UN. Pire, `.html_safe` en retirait un de
 * plus (`ERB::Util.unwrapped_html_escape` court-circuite sur un SafeBuffer),
 * si bien que les sites qui AVAIENT L'AIR défendus — `"…#{h(nom)}…".html_safe`
 * — étaient précisément les exploitables. Cet invariant de comptage, réparti
 * sur deux langages et trois parsers, était intenable : 23 des 66 appelants
 * l'avaient raté.
 *
 * Le sink a donc été supprimé plutôt que les appelants disciplinés :
 *
 *   - `message` est du TEXTE (jamais du HTML), rendu en nœuds texte. Il peut
 *     porter un slot `%{highlight}` et des `\n` (rendus en <br>).
 *   - `messageHighlight` est le fragment non fiable : inséré via `textContent`
 *     dans un <strong> créé par le JS. L'emphase est portée par le GABARIT,
 *     jamais par la donnée — donc l'injection est impossible par construction,
 *     quoi qu'envoie le serveur.
 *   - `messageTemplate` (contenu riche : liens, icônes, champs) est l'id d'un
 *     <template> RENDU PAR LE SERVEUR, cloné via `cloneNode`. Aucune chaîne
 *     n'est parsée, et l'échappement ERB y redevient NORMAL (le <strong> est du
 *     markup, `<%= nom %>` est échappé une fois et jamais décodé, puisque c'est
 *     du contenu d'élément et non un attribut).
 *
 * Un appelant non migré qui passerait du HTML dans `message` le verra rendu en
 * texte littéral : sûr, et visiblement dégradé. C'est délibéré — un échec
 * cosmétique bruyant vaut mieux qu'une faille silencieuse.
 *
 * Verrouillé par `test/assets/confirmation_modal_message_sink_test.rb` (le sink
 * n'existe plus) et `test/system/confirmation_modal_xss_test.rb` (preuve au
 * navigateur).
 *
 * Couleur d'action (`icon-color-value` : red, yellow, blue, green ; défaut
 * red) : icône, jetons et tracé du bouton viennent des cibles `variantTemplate`,
 * des <template> rendus par le composant. Aucun habillage en dur ici.
 *
 * Utilisation sur un lien/bouton de suppression :
 * <a href="..."
 *    data-controller="confirmation-modal-trigger"
 *    data-confirmation-modal-trigger-title-value="Confirmer la suppression"
 *    data-confirmation-modal-trigger-message-value="Supprimer %{highlight} ?"
 *    data-confirmation-modal-trigger-message-highlight-value="<%= contractor.contractor_name %>"
 *    data-confirmation-modal-trigger-confirm-text-value="Supprimer"
 *    data-confirmation-modal-trigger-method-value="delete"
 *    data-action="click->confirmation-modal-trigger#open">
 */
export default class extends Controller {
  static targets = [
    'modal',
    'overlay',
    'content',
    'title',
    'message',
    'confirmText',
    'confirmButton',
    'confirmIcon',
    'modalIcon',
    'modalIconBg',
    'variantTemplate',
  ];

  // URL et méthode HTTP de l'action à confirmer
  pendingUrl = null;
  pendingMethod = 'delete';
  pendingCsrfToken = null;
  pendingIconColor = 'red';

  connect() {
    // Écouter l'événement Escape pour fermer la modale
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  /**
   * Ouvre la modale avec les paramètres fournis
   * Appelé depuis un élément déclencheur externe
   */
  open(event) {
    event.preventDefault();

    const trigger = event.currentTarget;

    // Récupérer l'URL et la méthode
    this.pendingUrl = trigger.href || trigger.dataset.confirmationModalTriggerUrl;
    this.pendingMethod = trigger.dataset.confirmationModalTriggerMethod || 'delete';
    this.pendingIconColor = trigger.dataset.confirmationModalTriggerIconColor || 'red';

    // Récupérer le token CSRF pour les requêtes non-GET
    this.pendingCsrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // Mettre à jour le contenu de la modale
    if (trigger.dataset.confirmationModalTriggerTitle) {
      this.titleTarget.textContent = trigger.dataset.confirmationModalTriggerTitle;
    }

    // Contenu riche (liens, icônes, champs) : cloné depuis un <template> rendu
    // par le serveur. Sinon : gabarit texte + slot d'emphase.
    if (trigger.dataset.confirmationModalTriggerMessageTemplate) {
      this.renderMessageTemplate(trigger.dataset.confirmationModalTriggerMessageTemplate);
    } else if (trigger.dataset.confirmationModalTriggerMessage) {
      this.renderMessage(
        trigger.dataset.confirmationModalTriggerMessage,
        trigger.dataset.confirmationModalTriggerMessageHighlight
      );
    }

    if (trigger.dataset.confirmationModalTriggerConfirmText) {
      this.confirmTextTarget.textContent = trigger.dataset.confirmationModalTriggerConfirmText;
    }

    // Mettre à jour les styles selon la couleur
    this.updateModalStyles();

    // Afficher la modale
    this.show();
  }

  /**
   * Rend le message en NŒUDS TEXTE — aucune chaîne n'est jamais parsée en HTML.
   *
   * `template` est un gabarit écrit par un développeur (jamais de la donnée
   * utilisateur) ; `highlight` est le fragment non fiable, posé via textContent
   * dans un <strong> que le JS crée lui-même.
   *
   * @param {string} template - gabarit texte, peut contenir `%{highlight}` et des `\n`
   * @param {string|undefined} highlight - fragment mis en emphase (donnée non fiable)
   */
  renderMessage(template, highlight) {
    this.messageTarget.replaceChildren();

    // split() sur le GABARIT seul : un `%{highlight}` présent dans la donnée
    // non fiable ne peut donc pas ouvrir de second slot.
    template.split(HIGHLIGHT_SLOT).forEach((part, index) => {
      if (index > 0) {
        const strong = document.createElement('strong');
        strong.textContent = highlight ?? '';
        this.messageTarget.appendChild(strong);
      }
      this.appendTextWithLineBreaks(part);
    });
  }

  /**
   * Les `\n` du gabarit deviennent des <br> — la seule mise en forme admise
   * hors <template>, et elle ne parse rien.
   */
  appendTextWithLineBreaks(text) {
    text.split('\n').forEach((line, index) => {
      if (index > 0) this.messageTarget.appendChild(document.createElement('br'));
      if (line) this.messageTarget.appendChild(document.createTextNode(line));
    });
  }

  /**
   * Clone un <template> rendu par le serveur (contenu riche : liens, icônes,
   * champs moissonnés par confirm()). `cloneNode` copie des NŒUDS déjà parsés
   * par le parser HTML depuis la sortie ERB — il ne parse aucune chaîne.
   */
  renderMessageTemplate(templateId) {
    const template = document.getElementById(templateId);

    if (!(template instanceof HTMLTemplateElement)) {
      console.error(`Confirmation modal message template not found: ${templateId}`);
      return;
    }

    this.messageTarget.replaceChildren(template.content.cloneNode(true));
  }

  /**
   * Affiche la modale avec une animation
   */
  show() {
    this.modalTarget.classList.remove('hidden');

    // Forcer un reflow pour que la transition CSS fonctionne
    void this.modalTarget.offsetHeight;

    // Ajouter les classes d'animation (pas d'opacity-100 sur overlay pour garder le bg-opacity du template)
    this.contentTarget.classList.add('opacity-100', 'translate-y-0', 'sm:scale-100');

    // Empêcher le scroll du body
    document.body.classList.add('overflow-hidden');
  }

  /**
   * Ferme la modale
   */
  close() {
    // Retirer les classes d'animation
    this.contentTarget.classList.remove('opacity-100', 'translate-y-0', 'sm:scale-100');

    // Attendre la fin de la transition avant de cacher
    setTimeout(() => {
      this.modalTarget.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');

      // Réinitialiser les données
      this.pendingUrl = null;
      this.pendingMethod = 'delete';
      this.pendingCsrfToken = null;
    }, 200);
  }

  /**
   * Confirme l'action et exécute la requête
   */
  confirm() {
    if (!this.pendingUrl) {
      console.error('No URL to submit');
      this.close();
      return;
    }

    this.storeScrollPositionForOnboardingDelete();
    this.maybeStorePortalScrollAnchor();

    // Créer un formulaire pour soumettre la requête DELETE/POST/etc.
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = this.pendingUrl;

    // Ajouter le token CSRF
    if (this.pendingCsrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = this.pendingCsrfToken;
      form.appendChild(csrfInput);
    }

    // Ajouter le champ _method pour les requêtes DELETE, PATCH, PUT
    if (this.pendingMethod && this.pendingMethod.toLowerCase() !== 'post') {
      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = this.pendingMethod;
      form.appendChild(methodInput);
    }

    // Forward any named inputs embedded in the message area to the form
    this.messageTarget.querySelectorAll('input[name]').forEach((input) => {
      const field = document.createElement('input');
      field.type = 'hidden';
      field.name = input.name;
      field.value = input.type === 'checkbox' ? (input.checked ? 'true' : 'false') : input.value;
      form.appendChild(field);
    });

    // Soumettre le formulaire via requestSubmit pour que Turbo l'intercepte
    document.body.appendChild(form);
    form.requestSubmit();
    form.remove();

    this.close();
  }

  maybeStorePortalScrollAnchor() {
    const portalUrlPatterns = [
      'revoke_contact_portal_access',
      'disable_portal',
      'resend_portal_link',
      'send_portal_link',
    ];
    if (!portalUrlPatterns.some((p) => this.pendingUrl?.includes(p))) return;
    try {
      sessionStorage.setItem('portal:scroll-anchor', 'espace-client');
    } catch {
      // Best-effort UX enhancement: ignore storage failures.
    }
  }

  storeScrollPositionForOnboardingDelete() {
    if (!this.pendingUrl?.includes('/onboarding/') || !this.pendingUrl.includes('/delete_item')) {
      return;
    }

    try {
      sessionStorage.setItem(
        ONBOARDING_SCROLL_RESTORE_KEY,
        JSON.stringify({
          path: window.location.pathname,
          y: window.scrollY,
          timestamp: Date.now(),
        })
      );
    } catch {
      // Best-effort UX enhancement: ignore storage failures.
    }
  }

  /**
   * Gère les touches du clavier (Escape pour fermer)
   */
  handleKeydown(event) {
    if (event.key === 'Escape' && !this.modalTarget.classList.contains('hidden')) {
      this.close();
    }
  }

  /**
   * Habille la modale selon la couleur d'action du déclencheur (S13 / R-34).
   *
   * Tout vient des `<template data-confirmation-modal-target="variantTemplate">`
   * rendus par `Ui::ConfirmationModalComponent`, un par couleur : l'icône ronde
   * de l'en-tête (clonée, jamais parsée), les jetons de couleur de cette icône
   * et du bouton, le tracé de l'icône du bouton. Le JS ne porte ni couleur ni
   * tracé : la lint ERB ne lit pas ce fichier. Couleur inconnue ⇒ « red ».
   */
  updateModalStyles() {
    const variant = this.variantTemplateFor(this.pendingIconColor);
    if (!variant) return;

    const variants = this.variantTemplateTargets;

    if (this.hasModalIconBgTarget) {
      // Remplace l'icône : jamais d'accumulation d'une ouverture à l'autre.
      this.modalIconBgTarget.replaceChildren(variant.content.cloneNode(true));
      this.swapVariantClasses(
        this.modalIconBgTarget,
        variant.dataset.iconClasses,
        variants.map((template) => template.dataset.iconClasses)
      );
    }

    if (this.hasConfirmButtonTarget) {
      this.swapVariantClasses(
        this.confirmButtonTarget,
        variant.dataset.buttonClasses,
        variants.map((template) => template.dataset.buttonClasses)
      );
    }

    if (this.hasConfirmIconTarget) {
      // Pas de tracé pour cette couleur (yellow) ⇒ le bouton n'a pas d'icône.
      const path = variant.dataset.confirmIconPath;
      if (path) this.confirmIconTarget.setAttribute('d', path);
      this.confirmIconTarget.ownerSVGElement?.classList.toggle('hidden', !path);
    }
  }

  /** Le gabarit de la couleur demandée, sinon celui de « red » (comme avant S13). */
  variantTemplateFor(color) {
    const variants = this.hasVariantTemplateTarget ? this.variantTemplateTargets : [];
    const byColor = (wanted) => variants.find((template) => template.dataset.iconColor === wanted);

    return byColor(color) || byColor('red') || null;
  }

  /**
   * Retire les jetons de TOUTES les variantes puis pose ceux de la variante
   * choisie. Les classes de base (taille, forme, focus-visible) restent celles
   * du rendu serveur : le JS n'en garde aucune copie.
   */
  swapVariantClasses(element, chosen, allVariants) {
    const tokens = (classes) => (classes || '').split(/\s+/).filter(Boolean);
    const variantTokens = new Set(allVariants.flatMap(tokens));
    const kept = tokens(element.className).filter((token) => !variantTokens.has(token));

    element.className = [...kept, ...tokens(chosen)].join(' ');
  }
}
